/**
 * Layout and type constants, matching website-cloud's web/src/theme.ts so the
 * three sites read as one product.
 *
 * The design system's token files supply every colour; these are the handful of
 * metrics and type recipes the components need inline.
 */

import type React from 'react';

export const EASE = 'cubic-bezier(.22,.61,.36,1)';
export const HAIR = '1px solid var(--border-subtle)';

export const RADIUS = {
  panel: 22,
  card: 16,
  control: 10,
  pill: 999
} as const;

export const SHADOW = {
  hairline: '0 1px 0 rgba(40,28,45,.04)',
  md: '0 4px 14px rgba(40,28,45,.08)',
  lg: '0 18px 40px rgba(40,28,45,.14)',
  popover: '0 18px 44px rgba(40,28,45,.18)'
} as const;

/** The page is a single column. A status board is a list, and a status board
 *  that needs a sidebar has lost the plot. */
export const PAGE_WIDTH = 880;

/** Baloo 2 800. Titles in the Mauvely voice end in a full stop. */
export const display = (size: number): React.CSSProperties => ({
  fontFamily: "var(--font-display), 'Baloo 2', system-ui",
  fontWeight: 800,
  fontSize: size,
  lineHeight: size >= 32 ? 1.04 : 1.1,
  letterSpacing: '-.015em',
  color: 'var(--text-strong)',
  margin: 0
});

/** Eyebrow labels: mono, wide tracking, uppercase. */
export const mono: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontSize: 10.5,
  letterSpacing: '.17em',
  textTransform: 'uppercase',
  color: 'var(--text-faint)'
};

/** Metadata lines: mono, joined with `·`, never commas and never emoji. */
export const monoPlain: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontSize: 11,
  color: 'var(--text-muted)'
};

/**
 * Uptime percentages and latencies are tabular figures.
 *
 * Not a flourish: a column of proportional-width numerals visibly jitters as
 * the last digit changes, and this page refreshes every thirty seconds. Mono
 * plus `tabular-nums` keeps the column still.
 */
export const figure: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontVariantNumeric: 'tabular-nums',
  fontSize: 13,
  color: 'var(--text-body)'
};

export const tableHead: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontSize: 10,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  color: 'var(--text-quiet)'
};

export const panel: React.CSSProperties = {
  background: 'var(--surface-card)',
  borderRadius: RADIUS.panel,
  boxShadow: SHADOW.hairline
};
